/**
 * Bloomyer Header V2 — standalone JS
 * Handles: sticky, search modal, locale dropdowns, mobile drawer
 */

/* === Sticky Header === */
class BloomyerStickyHeader extends HTMLElement {
  constructor() {
    super();
    this.header = null;
    this.currentScrollTop = 0;
    this.headerBounds = {};
    this.stickyType = 'on-scroll-up';
    this.onScrollHandler = this.onScroll.bind(this);
  }

  connectedCallback() {
    this.header = this.querySelector('.bh2-section');
    if (!this.header) return;

    this.stickyType = this.dataset.stickyType || 'on-scroll-up';
    this.headerBounds = {};
    this.currentScrollTop = 0;

    this.setHeaderHeight();
    window.matchMedia('(max-width: 990px)').addEventListener('change', () => this.setHeaderHeight());

    if (this.stickyType === 'none') {
      // Sticky desactive : le header reste toujours en position absolute normale
      return;
    }

    if (this.stickyType === 'always') {
      // Toujours colle en haut, des le chargement, sans attendre le scroll
      this.header.classList.add('bh2-sticky');
      this.header.style.position = 'fixed';
      this.header.style.top = '0';
      return;
    }

    window.addEventListener('scroll', this.onScrollHandler, false);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  setHeaderHeight() {
    const wrapper = this.header.querySelector('.bh2-wrapper');
    if (wrapper) {
      document.documentElement.style.setProperty(
        '--bh2-header-height',
        wrapper.offsetHeight + 'px'
      );
    }
  }

  onScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    if (scrollTop > 100) {
      this.header.classList.add('bh2-sticky');
      this.header.style.position = 'fixed';
      this.header.style.top = '0';
    } else {
      this.header.classList.remove('bh2-sticky');
      this.header.style.position = 'absolute';
      this.header.style.top = 'calc(var(--announcement-bar-height, 0px) + var(--bh2-top-offset, 0px))';
    }

    this.currentScrollTop = scrollTop;
  }
}

if (!customElements.get('bloomyer-sticky-header')) {
  customElements.define('bloomyer-sticky-header', BloomyerStickyHeader);
}

/* === Search Modal === */
document.addEventListener('DOMContentLoaded', () => {
  const searchBtn = document.querySelector('.bh2-search-trigger');
  const searchModal = document.querySelector('.bh2-search-modal');
  const searchOverlay = document.querySelector('.bh2-search-overlay');
  const searchClose = document.querySelector('.bh2-search-close');
  const searchInput = document.querySelector('.bh2-search-input');

  function openSearch() {
    if (searchModal) searchModal.classList.add('bh2-open');
    if (searchOverlay) searchOverlay.classList.add('bh2-open');
    if (searchInput) setTimeout(() => searchInput.focus(), 100);
  }

  function closeSearch() {
    if (searchModal) searchModal.classList.remove('bh2-open');
    if (searchOverlay) searchOverlay.classList.remove('bh2-open');
  }

  if (searchBtn) searchBtn.addEventListener('click', openSearch);
  if (searchClose) searchClose.addEventListener('click', closeSearch);
  if (searchOverlay) searchOverlay.addEventListener('click', closeSearch);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeSearch();
  });
});

/* === Locale Dropdowns === */
document.addEventListener('DOMContentLoaded', () => {
  const localeWrappers = document.querySelectorAll('.bh2-locale-wrapper');

  localeWrappers.forEach((wrapper) => {
    const btn = wrapper.querySelector('.bh2-locale-btn');
    const dropdown = wrapper.querySelector('.bh2-locale-dropdown');

    if (!btn || !dropdown) return;

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      // Close other dropdowns
      localeWrappers.forEach((other) => {
        if (other !== wrapper) other.classList.remove('bh2-open');
      });
      wrapper.classList.toggle('bh2-open');
    });

    // Handle selection
    dropdown.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const value = link.getAttribute('data-value');
        const form = wrapper.closest('form');
        if (form) {
          const hiddenInput = form.querySelector('input[type="hidden"]');
          if (hiddenInput) {
            hiddenInput.value = value;
            form.submit();
          }
        }
        wrapper.classList.remove('bh2-open');
      });
    });
  });

  // Close dropdowns when clicking outside
  document.addEventListener('click', () => {
    localeWrappers.forEach((wrapper) => wrapper.classList.remove('bh2-open'));
  });
});

/* === Mobile Drawer ===
   Le drawer est téléporté sur <body> pour échapper au stacking context
   du groupe header Shopify (z-index: 3 vs announcement-bar z-index: 4)
*/
document.addEventListener('DOMContentLoaded', () => {
  const burger = document.querySelector('.bh2-burger');
  let drawer = document.querySelector('.bh2-mobile-drawer');

  // Téléporter le drawer directement sur <body> pour sortir du stacking context Shopify
  if (drawer && drawer.parentElement !== document.body) {
    document.body.appendChild(drawer);
  }

  const overlay = drawer ? drawer.querySelector('.bh2-mobile-drawer__overlay') : null;
  const closeBtn = drawer ? drawer.querySelector('.bh2-md-close') : null;

  function openDrawer() {
    if (drawer) {
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('bh2-open');
      document.body.style.overflow = 'hidden';
    }
  }

  function closeDrawer() {
    if (drawer) {
      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove('bh2-open');
      document.body.style.overflow = '';
    }
  }

  if (burger) burger.addEventListener('click', openDrawer);
  if (overlay) overlay.addEventListener('click', closeDrawer);
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

  if (drawer) {
    const drawerSearch = drawer.querySelector('.bh2-md-search-trigger');
    if (drawerSearch) {
      drawerSearch.addEventListener('click', () => {
        closeDrawer();
        const modal = document.querySelector('.bh2-search-modal');
        const searchOverlay = document.querySelector('.bh2-search-overlay');
        if (modal) modal.classList.add('bh2-open');
        if (searchOverlay) searchOverlay.classList.add('bh2-open');
        document.body.style.overflow = 'hidden';
        const input = modal ? modal.querySelector('.bh2-search-input') : null;
        if (input) setTimeout(() => input.focus(), 50);
      });
    }
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawer();
  });
});
